import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { apiClient, useAuthStore } from '@indipia/shared'
import { Alert, Badge, Combobox, Page, PageHeader, TextLink } from '@indipia/ui-core'
import type { NavFn, DirtyHandle } from '../pages/SchedulerPage'
import { useCommonCodes } from '../context/CommonCodesContext'
import { ErrorBanner } from '../components/AsyncState'
import { SettingsSaveBar } from '../components/SettingsSaveBar'
import { ORDER_MESSAGE } from '../constants/materialOrder.logic'

const MSG = ORDER_MESSAGE.settings
const SAVED_VISIBLE_MS = 2000

type Props = { navigate: NavFn; onDirtyChange?: (h: DirtyHandle | null) => void }

// 시공파트너 — /schedule/partners 프록시 응답(= sv-member PartnerInfo). GanttView·
// CalendarScheduleView와 동일 계약. specialtyTypes=주작업공종(쉼표 구분 코드),
// subWorkTypes=부작업공종(코드 목록). 후보는 주·부 작업공종 어느 쪽이든 해당 공종을 가진 파트너.
// 참고지표(avgRating·partnerBizYn·sigungus)는 후보 드롭다운에 표시한다.
interface Partner {
  partnerId:      number
  partnerName:    string
  mobile?:        string
  specialtyTypes?: string | null
  subWorkTypes?:  string[] | null
  avgRating?:     number | null              // 평균별점(평가 없으면 null)
  reviewCount?:   number | null              // 평가 건수
  collabCount?:   number | null              // 직전 12개월 협업 횟수(일정 확정 배정 건수, 서버 계산)
  partnerBizYn?:  string | null              // Y=업체 / N=개인
  partnerType?:   string | null              // 파트너 유형 E01_PARTNER_TYPE (FOREMAN=개인/COMPANY=업체, null=미지정)
  dealStatus?:    string | null              // 거래 상태 E01_PARTNER_DEAL_STATUS (ACTIVE/SUSPENDED)
  sigungus?:      { code: string; name: string }[] | null  // 활동지역
  defectReportRate?: number | null           // 하자보고 %(정수 0~100, 집계 없으면 null)
  createdAt?:     string | null              // 파트너 등록일시(정렬 타이브레이커: 먼저 등록 우선)
}

// 공종별 주시공파트너 매핑(GET /schedule/main-partners) — partnerId null 은 미지정
interface MainPartner {
  workTypeCode: string
  partnerId:    number | null
}

// PUT /schedule/main-partners 응답 — items=저장 후 현재 지정 목록, rejected=서버 검증 거절(저장 제외, 부분 실패 허용)
interface MainPartnerRejected {
  workTypeCode: string
  partnerId:    number | null
  reason:       string
}
interface MainPartnerSaveResult {
  items:    MainPartner[]
  rejected: MainPartnerRejected[]
}

// Combobox 배정 해제 옵션 값 — 빈 문자열은 "미선택"이라 센티넬을 따로 둔다
// (CalendarScheduleView·SigongPopup과 동일 패턴)
const PARTNER_NONE = '__none__'

// 활동지역 요약 — 시군구명("서울특별시 강남구")에서 시·도만 축약("서울"), 유니크 묶음.
function regionSummary(sigungus?: { code: string; name: string }[] | null): string {
  if (!sigungus || sigungus.length === 0) return '활동지역 미등록'
  const sidos = Array.from(new Set(sigungus.map(s => {
    const head = (s.name || '').split(' ')[0]
    return head.replace(/(특별자치시|특별자치도|특별시|광역시|도)$/, '')
  }).filter(Boolean)))
  return sidos.join('·')
}

export function PartnerAssignView({ navigate, onDirtyChange }: Props) {
  const routerNavigate = useNavigate()
  const user = useAuthStore(s => s.user)
  const { codes } = useCommonCodes()
  // 스케줄 전용 행정 공정(세대동의서청구 등, scheduleOnlyYn='Y')은 시공파트너 배정 대상이 아니라 제외
  const workTypes = (codes?.workTypes ?? []).filter(w => w.scheduleOnlyYn !== 'Y')

  const [partners,   setPartners]   = useState<Partner[]>([])
  // 공종코드 → 주시공 partnerId
  const [assigned,   setAssigned]   = useState<Map<string, number>>(new Map())
  // 서버에 저장된 지정(변경 비교 기준)
  const [baseAssigned, setBaseAssigned] = useState<Map<string, number>>(new Map())
  const [saving,     setSaving]     = useState(false)
  const [saved,      setSaved]      = useState(false)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // SIF-1 '자재파트너가 시공' 공종코드(H-5 발주·입고설정 교차 조회) — 이 공종은 시공파트너 선택 대신 '—' 표시
  const [constructCodes, setConstructCodes] = useState<Set<string>>(new Set())
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState('')
  const [loadError,  setLoadError]  = useState(false)
  const [reloadKey,  setReloadKey]  = useState(0)
  // 공종코드 → 서버 검증 거절 사유(해당 행 오류 표시). 그 행을 다시 고르면 지워진다.
  const [rejected,   setRejected]   = useState<Map<string, string>>(new Map())

  const headers = {
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }

  // 시공파트너 목록 — 공종 전문분야 필터의 후보 (GanttView 등과 동일 /schedule/partners)
  useEffect(() => {
    if (!user?.compId) return
    apiClient.get<{ data: Partner[] }>('/schedule/partners', { headers })
      .then(res => setPartners(res.data.data ?? []))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.compId])

  // SIF-1 발주·입고설정 교차 조회 — '자재파트너가 시공'(constructByVendorYn='Y') 공종을 식별(실패 시 빈 집합 → 일반 공종으로 표시)
  useEffect(() => {
    apiClient.get<{ data: { orderSettings: { workTypeCode: string; constructByVendorYn?: string | null }[] } }>('/schedule/settings', { headers })
      .then(res => {
        const list = res.data?.data?.orderSettings ?? []
        setConstructCodes(new Set(list.filter(s => s.constructByVendorYn === 'Y').map(s => s.workTypeCode)))
      })
      .catch(() => setConstructCodes(new Set()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  // 기존 매핑 로드
  useEffect(() => {
    setLoading(true)
    setLoadError(false)
    apiClient.get<{ data: MainPartner[] }>('/schedule/main-partners', { headers })
      .then(res => {
        const rows = res.data?.data ?? []
        const next = new Map<string, number>()
        rows.forEach(r => { if (r.partnerId != null) next.set(r.workTypeCode, r.partnerId) })
        setAssigned(next)
        setBaseAssigned(new Map(next))
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  // 해당 공종을 주작업(specialtyTypes) 또는 부작업(subWorkTypes)으로 가진 파트너만 후보로 거르고,
  // 룰정의서 930 #2 정렬(주작업 우선 → 협업횟수 → 등록일 순, 별점은 보지 않음)로 정렬한다. main=이 공종이 주작업인지.
  // 거래중지(dealStatus=SUSPENDED) 파트너는 후보에서 제외(FR-B-56). API-033 은 거래중지도 내려주므로 화면에서 거른다.
  // 단, 이미 이 공종의 주시공으로 지정돼 있으면 선택값이 비어 보이지 않도록 남긴다(거래중지 배지로 구분).
  const candsFor = (workTypeCode: string): { p: Partner; main: boolean }[] =>
    partners
      .filter(p => p.dealStatus !== 'SUSPENDED' || assigned.get(workTypeCode) === p.partnerId)
      .filter(p => {
        const inMain = p.specialtyTypes ? p.specialtyTypes.split(',').includes(workTypeCode) : false
        const inSub  = p.subWorkTypes ? p.subWorkTypes.includes(workTypeCode) : false
        return inMain || inSub
      })
      .map(p => ({ p, main: p.specialtyTypes ? p.specialtyTypes.split(',').includes(workTypeCode) : false }))
      .sort((a, b) => {
        if (a.main !== b.main) return a.main ? -1 : 1
        // 협업횟수 내림차순 → 등록일 순(먼저 등록, null 은 맨 뒤)
        const ca = a.p.collabCount ?? 0
        const cb = b.p.collabCount ?? 0
        if (ca !== cb) return cb - ca
        const ta = a.p.createdAt ? Date.parse(a.p.createdAt) : NaN
        const tb = b.p.createdAt ? Date.parse(b.p.createdAt) : NaN
        if (Number.isNaN(ta) && Number.isNaN(tb)) return 0
        if (Number.isNaN(ta)) return 1
        if (Number.isNaN(tb)) return -1
        return ta - tb
      })

  const setPartnerFor = (code: string, partnerId: number | null) => {
    setSaved(false)
    setRejected(prev => {
      if (!prev.has(code)) return prev
      const next = new Map(prev)
      next.delete(code)
      return next
    })
    setAssigned(prev => {
      const next = new Map(prev)
      if (partnerId == null) next.delete(code)
      else next.set(code, partnerId)
      return next
    })
  }

  useEffect(() => () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current) }, [])

  // 바꾼 공종 — 자재파트너가 시공하는 공종은 지정 대상이 아니라 저장에서 제외(서버에 저장된 값도 건드리지 않는다)
  const changedCodes = workTypes
    .map(w => w.workTypeCode)
    .filter(code => !constructCodes.has(code) && (assigned.get(code) ?? null) !== (baseAssigned.get(code) ?? null))
  const changedCount = changedCodes.length

  const handleReset = () => {
    setError('')
    setSaved(false)
    setRejected(new Map())
    setAssigned(new Map(baseAssigned))
  }

  /** 실패 시 오류를 표시하고 던진다(이탈 확인창이 이동을 막도록) */
  const handleSave = async () => {
    setError('')
    if (changedCodes.length === 0) return
    setSaving(true)
    try {
      const items = changedCodes.map(code => ({ workTypeCode: code, partnerId: assigned.get(code) ?? null }))
      const res = await apiClient.put<{ data: MainPartnerSaveResult }>('/schedule/main-partners', { items }, { headers })
      const result = res.data?.data
      if (!result) throw new Error('empty-main-partner-response')
      const rejectedMap = new Map<string, string>()
      ;(result.rejected ?? []).forEach(r => rejectedMap.set(r.workTypeCode, r.reason))
      setRejected(rejectedMap)
      // 저장된 지정으로 기준을 갱신 — 거절된 행은 고른 값을 그대로 두어 오류와 함께 보이고, 계속 '바뀐 공종'으로 남는다.
      const base = new Map<string, number>()
      ;(result.items ?? []).forEach(r => { if (r.partnerId != null) base.set(r.workTypeCode, r.partnerId) })
      setBaseAssigned(base)
      if (rejectedMap.size > 0) {
        setError(`${rejectedMap.size}개 공종은 저장하지 못했어요. 표시된 사유를 확인하세요.`)
        return
      }
      setSaved(true)
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => setSaved(false), SAVED_VISIBLE_MS)
    } catch (e) {
      setError('저장 중 오류가 발생했습니다.')
      throw e
    } finally {
      setSaving(false)
    }
  }

  // 이탈 보호 — 바꾼 공종이 있을 때만 부모에 보고
  const saveRef = useRef(handleSave)
  saveRef.current = handleSave
  const resetRef = useRef(handleReset)
  resetRef.current = handleReset
  const reportDirty = useCallback((h: DirtyHandle | null) => { onDirtyChange?.(h) }, [onDirtyChange])
  useEffect(() => {
    if (changedCount === 0) { reportDirty(null); return }
    reportDirty({
      count: changedCount,
      subject: MSG.dirtySubject,
      save: async () => { await saveRef.current() },
      discard: () => resetRef.current(),
    })
  }, [changedCount, reportDirty])
  useEffect(() => () => reportDirty(null), [reportDirty])
  useEffect(() => {
    if (changedCount === 0) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [changedCount])

  return (
    <div style={{ minHeight: '100%' }}>
      {loading ? (
        <div aria-busy="true" style={{ textAlign: 'center', padding: '60px 0', color: 'var(--shell-hint)', fontSize: 13 }}>불러오는 중...</div>
      ) : (
        <Page width="lg">
          <PageHeader title="공종별 주 시공파트너" subtitle="공종마다 주로 함께 일하는 시공파트너를 지정하세요. 지정하면 스케줄 생성 시 자동으로 배정됩니다." />

          {loadError && (
            <div style={{ marginBottom: 16 }}>
              <ErrorBanner message="저장된 설정을 불러오지 못했습니다." onRetry={() => setReloadKey(k => k + 1)} />
            </div>
          )}

          {/* 안내 메모 — 표 위 */}
          <Alert kind="info" className="mb-4">
            <span>각 공종에 그 공종을 <strong>주·부 작업공종으로 가진 시공파트너</strong>만 후보로 표시됩니다. 지정은 공종당 1명이며, 빈 값으로 두면 지정이 해제됩니다.<br />후보가 없는 공종은 시공파트너를 먼저 등록하면 선택할 수 있어요.</span>
          </Alert>

          <div style={{ background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 16, overflow: 'hidden', marginBottom: 12 }}>
            {/* 테이블 — 좌: 공종명 / 우: 주 시공파트너 선택 */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--shell-bg)' }}>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', letterSpacing: '-.2px', width: '38%' }}>공종명</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', letterSpacing: '-.2px' }}>주 시공파트너</th>
                </tr>
              </thead>
              <tbody>
                {workTypes.map((w, i) => {
                  const color     = w.workTypeColor ?? 'var(--shell-on-surface-2)'
                  const isConstruct = constructCodes.has(w.workTypeCode)
                  const partnerId = assigned.get(w.workTypeCode) ?? null
                  const cands     = candsFor(w.workTypeCode)
                  const candMap   = new Map(cands.map(c => [String(c.p.partnerId), c]))
                  const current   = partnerId != null ? candMap.get(String(partnerId)) : undefined
                  const options   = [
                    { value: PARTNER_NONE, label: '지정 안 함' },
                    ...cands.map(c => ({ value: String(c.p.partnerId), label: c.p.partnerName })),
                  ]
                  const nameCell = (
                    <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>
                      {/* 자재파트너가 시공 공종은 선택 대상이 아니라 흐리게(회색 점·글자) */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: isConstruct ? 'var(--shell-hint)' : color, flexShrink: 0 }} />
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: isConstruct ? 'var(--shell-hint)' : 'var(--shell-on-surface)' }}>{w.workTypeName}</span>
                      </div>
                    </td>
                  )
                  return (
                    <tr key={w.workTypeCode} style={{ borderTop: i > 0 ? '1px solid var(--shell-border)' : 'none', background: isConstruct ? 'var(--shell-bg)' : undefined }}>
                      {nameCell}
                      <td style={{ padding: '10px 16px' }}>
                        {isConstruct ? (
                          // SIF-1 '자재파트너가 시공' — 시공파트너 선택 없이 '—' + 발주-입고설정 안내(비활성, 링크로 이동)
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span aria-hidden="true" style={{ fontSize: 14, color: 'var(--shell-hint)' }}>—</span>
                            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>
                              자재파트너가 시공 · 스튜디오 설정 &gt;&gt;{' '}
                              <TextLink onClick={() => navigate('settings-order')} style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700 }}>발주-입고설정</TextLink>
                              에서 정해요
                            </span>
                          </div>
                        ) : cands.length === 0 ? (
                          // 후보 0명 — 드롭다운 대신 "나의 시공파트너" 신규등록으로 보내는 링크
                          <div style={{ lineHeight: 1.5 }}>
                            <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>이 공종의 시공파트너가 아직 없어요</div>
                            <TextLink onClick={() => routerNavigate('/partners/new')} style={{ display: 'inline-block', marginTop: 2, fontSize: 'var(--type-caption-size)' }}>
                              시공파트너 등록 →
                            </TextLink>
                          </div>
                        ) : (
                          <>
                          <Combobox
                            value={partnerId != null ? String(partnerId) : ''}
                            displayValue={current
                              ? `${current.p.partnerName} · ${current.main ? '주작업' : '부작업'}`
                              : ''}
                            options={options}
                            error={rejected.get(w.workTypeCode)}
                            emptyText="등록된 시공파트너가 없습니다"
                            placeholder="시공파트너 선택"
                            onChange={(val) => setPartnerFor(w.workTypeCode, val === PARTNER_NONE ? null : Number(val))}
                            className="!mb-0 !max-w-none"
                            renderOption={(opt) => {
                              if (opt.value === PARTNER_NONE) {
                                return <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)' }}>지정 안 함</span>
                              }
                              const c = candMap.get(opt.value)
                              if (!c) return opt.label
                              const p = c.p
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: 'var(--type-body-sm-size)', lineHeight: 1.3 }}>
                                    <b style={{ marginRight: 2, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{p.partnerName}</b>
                                    {p.partnerType === 'FOREMAN' && <Badge variant="outline">개인</Badge>}
                                    {p.partnerType === 'COMPANY' && <Badge variant="outline">업체</Badge>}
                                    {p.dealStatus === 'SUSPENDED' && <Badge status="danger">거래중지</Badge>}
                                    <Badge status={c.main ? 'accent' : 'info'} variant={c.main ? undefined : 'soft'}>{c.main ? '주작업' : '부작업'}</Badge>
                                  </div>
                                  <div style={{ fontSize: 'var(--type-caption-size)', lineHeight: 1.3, color: 'var(--shell-on-surface-2)' }}>
                                    {p.avgRating != null ? (
                                      <>
                                        <span style={{ color: 'var(--status-warning-500)' }}>★</span>
                                        <b style={{ fontWeight: 700, color: 'var(--shell-on-surface)' }}> {p.avgRating.toFixed(1)}</b>
                                        {p.reviewCount != null && ` (${p.reviewCount}건)`}
                                      </>
                                    ) : '평가 없음'}
                                    {(p.collabCount ?? 0) > 0 && ` · 협업 ${p.collabCount}회`}
                                    {p.defectReportRate != null && ` · 하자보고 ${Math.round(p.defectReportRate)}%`}
                                    {' · '}{regionSummary(p.sigungus)}
                                  </div>
                                </div>
                              )
                            }}
                          />
                          <div style={{ marginTop: 4, fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>후보 {cands.length}명</div>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {!loadError && (
            <SettingsSaveBar
              changedCount={changedCount}
              saving={saving}
              saved={saved}
              error={error || null}
              onSave={() => { void handleSave().catch(() => undefined) }}
            />
          )}

        </Page>
      )}
    </div>
  )
}
